import { escapeXml } from '@util/getURL';
import { BLOCKS, Document, INLINES } from '@contentful/rich-text-types';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';

// Static styles for server-side rendering
const styles = `
  h1.header1 {
    display: block;
    font-size: 60px;
    font-weight: bold;
  }

  h2.header2 {
    display: block;
    font-size: 48px;
    font-weight: bold;
  }

  h3.header3 {
    display: block;
    font-size: 40px;
    font-weight: bold;
  }

  h4.header4 {
    display: block;
    font-size: 32px;
    font-weight: bold;
  }

  h5.header5 {
    display: block;
    font-size: 20px;
    line-height: 24px;
  }

  h6.eyebrow {
    font-size: 14px;
    color: #e60000;
    text-transform: uppercase;
  }

  p.paragraph {
    display: block;
    font-size: 14px;
  }

  ul.ul {
    list-style: initial;
    font-size: 14px;
  }

  ol.ol {
    list-style-type: decimal;
    font-size: 14px;
  }

  .quote p {
    font-size: 32px;
    font-weight: bold;
  }

  a.link {
    font-weight: 200;
    font-size: 14px;
  }
  
  .content-container p {
    margin-bottom: 0;
  }

  .grid-section {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;
    margin-top: 32px;
    margin-bottom: 32px;
  }

  .grid-section.reverse > div:nth-child(2) {
    order: -1;
  }
  
  .container-text-image {
    display: grid;
    grid-template-columns: 1fr;
    align-items: center;
    gap: 32px;
  }

  .text-column {
    flex: 1;
    p.paragraph {
      margin-bottom: 0;
    }
  }

  .image-column {
    flex: 0 0 auto;
    height: auto;
    position: relative;
  }

  .image-column img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  @media screen and (min-width: 768px) {
    .content-container {
      grid-column: span 2;
    }
    .grid-section {
      grid-template-columns: 1fr 1fr 1fr;
      align-items: start;
      gap: 32px;
    }
  }

  @media screen and (min-width: 1200px) {
    .content-container {
      grid-column: span 1;
    }
    .grid-section {
      grid-template-columns: 1fr 1fr;
      align-items: center;
      gap: 48px;
    }
    .container-text-image {
      display: flex;
      align-items: flex-start;
    }
    .image-column {
      height: 300px;
    }
  }
`;

const aspectBoxStyles = `
  position: relative;
  padding-top: 100%;
  height: 0;
  overflow: hidden;
`;

const imageStyles = `
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const paddingCss = ({ $ratio = 1.618 }: { $ratio?: number | number[] }) => {
  if (typeof $ratio === 'number') {
    const validRatio = Math.max($ratio, 0.01);
    return `padding-top: ${100 / validRatio}%;`;
  } else if (Array.isArray($ratio)) {
    return $ratio
      .map((r) => {
        const validRatio = Math.max(r || 0.01, 0.01);
        return `padding-top: ${100 / validRatio}%;`;
      })
      .join(' ');
  } else {
    return `padding-top: ${100 / 1.618}%;`; // Default to golden ratio
  }
};

export const getHostWithProtocol = (shopname?: string, useDefaultBlogs?: boolean) => {
  const host = typeof window !== 'undefined' ? window?.location?.host : undefined;
  const shopPrepend = shopname && !useDefaultBlogs ? `${shopname}.` : '';

  if (host && host?.includes('evrealestate.com')) {
    return `https://${shopPrepend}evrealestate.com`;
  } else if (host?.includes('digital-exp.evipscloud-uat')) {
    return `https://${shopPrepend}digital-exp.evipscloud-uat.com`;
  } else if (host?.includes('digital-exp.evipscloud-dev')) {
    return `https://${shopPrepend}digital-exp.evipscloud-dev.com`;
  } else if (host?.includes('dev.evadigitalexperience.com')) {
    return `https://${shopPrepend}dev.evadigitalexperience.com`;
  } else if (host?.includes('uat.evadigitalexperience.com')) {
    return `https://${shopPrepend}uat.evadigitalexperience.com`;
  } else if (host?.includes('localhost:3000')) {
    return `http://${shopPrepend}localhost:3000`;
  }
};

export const convertTagNameToSlug = (name: string) => {
  // convert name to lowercase
  const lower = name.toLowerCase();
  //remove ampersands and replace with nothing
  const ampersand = lower.replace(/&/g, 'and');
  // convert o with umlaut to o
  const umlaut = ampersand.replace(/ö/g, 'o');
  // convert spaces to hyphens
  const hyphen = umlaut.replace(/ /g, '-');
  // remove all non-alphanumeric characters
  const alphanumeric = hyphen.replace(/[^a-z0-9-]/g, '');

  return alphanumeric;
};

export function addLeadingZero(num) {
  num = num.toString();
  while (num.length < 2) num = '0' + num;
  return num;
}

export function buildRFC822Date(dateString: string) {
  const dayStrings = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthStrings = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const timeStamp = Date.parse(dateString);
  const date = new Date(timeStamp);

  const day = dayStrings[date.getUTCDay()];
  const dayNumber = addLeadingZero(date.getUTCDate());
  const month = monthStrings[date.getUTCMonth()];
  const year = date.getUTCFullYear();

  const hours = addLeadingZero(date.getUTCHours());
  const minutes = addLeadingZero(date.getUTCMinutes());
  const seconds = addLeadingZero(date.getUTCSeconds());

  // Returns a string like: "Tue, 18 Feb 2025 00:00:00 GMT"
  return `${day}, ${dayNumber} ${month} ${year} ${hours}:${minutes}:${seconds} GMT`;
}

const renderImageStack = (imgs: any[]) => {
  if (!imgs || !Array.isArray(imgs)) {
    return '';
  }

  const imageAspectRatios = {
    1: [
      // image length of 1, etc
      [2 / 1, 3 / 1, 3 / 1],
    ],
    2: [
      [2 / 1, 3 / 1, 3 / 1],
      [2 / 1, 3 / 1, 3 / 1],
    ],
    3: [
      [2 / 1, 3 / 1, 3 / 1],
      [1, 3 / 2, 3 / 2],
      [1, 3 / 2, 3 / 2],
    ],
  };
  const ratios = imageAspectRatios[imgs.length] || [];
  const gridStyles = `
    display: grid;
    gap: 16px;
    margin-top: 32px;
    margin-bottom: 32px;
    grid-template-columns: ${imgs.length === 1 ? '1fr' : imgs.length === 2 ? '1fr 1fr' : '1fr 1fr'};
  `;

  const gridItems = imgs.map((img, ind) => {
    const ratio = ratios[ind] || 1;

    let imgUrl;
    if (typeof img === 'object') {
      imgUrl = img?.fields?.image?.fields?.file?.url || img?.image;
    } else {
      imgUrl = img;
    }
    if (!imgUrl) return '';

    const paddingTop = paddingCss({ $ratio: ratio });
    const gridColumn =
      imgs.length === 2 && ind === 0
        ? 'span 2'
        : imgs.length === 3 && ind === 0
        ? 'span 2'
        : 'span 1';

    return `
      <div style="
        position: relative; 
        ${paddingTop}; 
        height: 0; 
        overflow: hidden; 
        grid-column: ${gridColumn};
      ">
        <img 
          src="${imgUrl.startsWith('//') ? `https:${imgUrl}` : imgUrl}"
          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover;" 
          alt="" 
        />
      </div>
    `;
  });

  return `
    <div style="${gridStyles}">
      ${gridItems.join('')}
    </div>
  `;
};

const generateHtmlForRSS = (richText: Document): string => {
  const options = {
    renderNode: {
      [BLOCKS.HEADING_1]: (node, next) => `<h1 class="header1">${next(node.content)}</h1>`,
      [BLOCKS.HEADING_2]: (node, next) => `<h2 class="header2">${next(node.content)}</h2>`,
      [BLOCKS.HEADING_3]: (node, next) => `<h3 class="header3">${next(node.content)}</h3>`,
      [BLOCKS.HEADING_4]: (node, next) => `<h4 class="header4">${next(node.content)}</h4>`,
      [BLOCKS.HEADING_5]: (node, next) => `<h5 class="header5">${next(node.content)}</h5>`,
      [BLOCKS.HEADING_6]: (node, next) => `<h6 class="eyebrow">${next(node.content)}</h6>`,
      [BLOCKS.PARAGRAPH]: (node, next) => `<p class="paragraph">${next(node.content)}</p>`,
      [BLOCKS.UL_LIST]: (node, next) => `<ul class="ul">${next(node.content)}</ul>`,
      [BLOCKS.OL_LIST]: (node, next) => `<ol class="ol">${next(node.content)}</ol>`,
      [BLOCKS.QUOTE]: (node, next) => `<div class="quote">${next(node.content)}</div>`,
      [BLOCKS.LIST_ITEM]: (node, next) => `<li>${next(node.content)}</li>`,
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const contentTypeId = node?.data?.target?.sys?.contentType?.sys?.id;
        if (contentTypeId === 'blogTextAndImage') {
          const { header, richText, imgFocalPoint, reverse } = node?.data?.target?.fields || {};
          const gridClass = reverse ? 'grid-section reverse' : 'grid-section';
          const richTextHtml = richText
            ? documentToHtmlString(richText) // Recursively convert nested rich text
            : '';
          const imageHtml = imgFocalPoint?.fields?.image?.fields?.file?.url
            ? `
            <div style="${aspectBoxStyles}">
              <img 
                src="${
                  imgFocalPoint?.fields?.image?.fields?.file?.url?.startsWith('//')
                    ? `https:${imgFocalPoint?.fields?.image?.fields?.file?.url}`
                    : imgFocalPoint?.fields?.image?.fields?.file?.url
                }"
                alt="" 
                style="${imageStyles}" 
              />
            </div>
          `
            : '';

          return `
            <div class="${gridClass}">
              <div class="content-container">
                ${header ? `<h3>${header}</h3>` : ''}
                ${richTextHtml}
              </div>
              ${imageHtml}
            </div>
          `;
        } else if (contentTypeId === 'imageStack') {
          const images = node?.data?.target?.fields?.imgs || [];
          return renderImageStack(images);
        }
        return '';
      },
      [BLOCKS.EMBEDDED_ASSET]: (node) =>
        `<img 
          src="${
            node?.data?.target?.fields?.file?.url?.startsWith('//')
              ? `https:${node?.data?.target?.fields?.file?.url}`
              : node?.data?.target?.fields?.file?.url
          }"
          alt="${node?.data?.target?.fields?.title || 'Embedded asset'}"
        />`,
      [INLINES.HYPERLINK]: (node, next) =>
        `<a href="${node?.data?.uri}" class="link">${next(node.content)}</a>`,
    },
  };

  return documentToHtmlString(richText, options);
};

export function buildRssItems(items: any[], baseUrl: string) {
  return items
    ?.map((item) => {
      const htmlBodyContent = generateHtmlForRSS(item?.bodyText);
      const htmlDescription = `
      <div class="container-text-image">
        <div class="text-column">
          ${generateHtmlForRSS(item?.description)}
        </div>
        <div class="image-column">
          <img
            src="${item?.blogImg?.startsWith('//') ? `https:${item?.blogImg}` : item?.blogImg}" 
            style="width: 100%; height: 100%; object-fit: contain; display: block;"
           alt="Embedded asset"/>
        </div>
      </div>
    `;
      const htmlStyles = `<style>${styles}</style>`;
      return `
      <item>
        <title>${escapeXml(item?.title)}</title>
        <description><![CDATA[ <img src="${
          item?.blogImg?.startsWith('//') ? `https:${item?.blogImg}` : item?.blogImg
        }" />${documentToPlainTextString(item?.description)}${documentToPlainTextString(
        item?.bodyText,
      )} ]]></description>
        <content:encoded><![CDATA[${htmlStyles}${htmlDescription}${htmlBodyContent}]]></content:encoded>
        <link>${baseUrl}/blog/${item?.slug}</link>
        <guid isPermaLink="true">${baseUrl}/blog/${item?.slug}</guid>
        <pubDate>${buildRFC822Date(item?.createdAt)}</pubDate>
      </item>
      `;
    })
    .join('');
}

export function buildRssFeedXml(
  blogPosts: any[],
  baseUrl: string,
  channelTitle = 'Engel & Völkers',
) {
  const mappedBlogPosts = blogPosts
    ?.filter((item: any) => item.fields)
    .map((item: any) => ({
      blogImg: item?.fields?.img?.fields?.file?.url,
      title: item.fields?.title,
      createdAt: item.sys.createdAt,
      bodyText: item.fields?.bodyText,
      slug: item.fields?.slug,
      description: item.fields?.description,
    }));
  return `<?xml version="1.0" encoding="UTF-8" ?>
  <rss version="2.0"
    xmlns:atom="http://www.w3.org/2005/Atom"
    xmlns:content="http://purl.org/rss/1.0/modules/content/"
    xmlns:sy="http://purl.org/rss/1.0/modules/syndication/"
    xmlns:dc="http://purl.org/dc/elements/1.1/"
  >
  <channel>
    <title>${escapeXml(`${channelTitle} Blogs`)}</title>
    <atom:link href="${baseUrl}/blog/rss-feed" rel="self" type="application/rss+xml" />
    <link>${baseUrl}</link>
    <description>RSS XML feeds of ${escapeXml(`${channelTitle} Blogs`)}</description>
    ${buildRssItems(mappedBlogPosts, baseUrl)}
  </channel>
  </rss>`;
}
